import React, { useEffect, useRef } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import menuOverlayService from "../header/MenuOverlayService";
import LuigisboxAutocompleteCategoriesBlock from "./LuigisboxAutocompleteCategoriesBlock";
import { LuigisboxAutocompleteData, LuigisboxHitItem } from "./luigisboxAutocompleteTypes";
import LuigisboxAutocompleteProductsBlock from "./LuigisboxAutocompleteProductsBlock";
import LuigisboxAutocompleteTrendingBlock from "./LuigisboxAutocompleteTrendingBlock";
import LuigisboxAutocompleteRelatedBlock from "./LuigisboxAutocompleteRelatedBlock";
import luigisboxAnanyticsService from "./luigisboxAnanyticsService";

interface LuigisboxAutocompleteProperties {
    readonly trackerId: string;
    readonly hostname: string;
    readonly isOpened: boolean;
    readonly query: string;
    readonly onQueryChange: (q: string) => void;
    readonly categoryPath?: string;
    readonly onHide: () => void;
    readonly onSubmitForm?: (query: string) => void;
}

const TOTAL_ITEMS = 5;
const TOTAL_CATEGORIES = 5;
const TOTAL_QUERIES = 5;

const handleAnchorClick = (url: string): void => {
    luigisboxAnanyticsService.sendAutocompleteClickEvent(url);
    globalThis.location.href = url;
};

const useLuigisboxAutocomplete = (
    query: string,
    categoryPath: string | undefined,
    trackerId: string,
    hostname: string,
): {
    readonly categories: LuigisboxHitItem<"category">[];
    readonly products: LuigisboxHitItem<"item">[];
    readonly relatedQueries: LuigisboxHitItem<"query">[];
    readonly isLoading: boolean;
    readonly isSuccess: boolean;
} => {
    const { data, isLoading, isSuccess } = useQuery<LuigisboxAutocompleteData>({
        queryKey: ["luigisbox-autocomplete", query, categoryPath],
        queryFn: () => fetch(
            `https://live.luigisbox.com/autocomplete/v2?tracker_id=${trackerId}&type=item:${TOTAL_ITEMS},category:${TOTAL_CATEGORIES},query:${TOTAL_QUERIES}&q=${query}${
                categoryPath !== undefined
                    ? `&f[]=category:${categoryPath}`
                    : ""
            }&hostname=${hostname}`,
        ).then((response) => response.json() as Promise<LuigisboxAutocompleteData>),
        enabled: query.length > 0,
        staleTime: 1000 * 60 * 60 * 24,
        gcTime: 1000 * 60 * 60 * 24,
        placeholderData: keepPreviousData,
    });

    try {
        return {
            categories:
                    (data?.hits.filter((hit) => hit.type === "category") as
                    | LuigisboxHitItem<"category">[]
                    | undefined) ?? [],
            products:
                    (data?.hits.filter((hit) => hit.type === "item") as
                    | LuigisboxHitItem<"item">[]
                    | undefined) ?? [],
            relatedQueries:
                    (data?.hits.filter((hit) => hit.type === "query") as
                    | LuigisboxHitItem<"query">[]
                    | undefined) ?? [],
            isLoading,
            isSuccess,
        };
    } catch (error) {
        if (error instanceof TypeError) {
            return {
                categories: [],
                products: [],
                relatedQueries: [],
                isLoading,
                isSuccess,
            };
        }

        throw error;
    }
};

export default function LuigisboxAutocomplete({
    query,
    onQueryChange,
    isOpened,
    categoryPath,
    onHide,
    onSubmitForm,
    trackerId,
    hostname,
}: LuigisboxAutocompleteProperties): React.JSX.Element | undefined {
    const { t } = useTranslation("header");

    const inputReference = useRef<HTMLInputElement>(null);
    const eventTimerId = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const resultsBoxReference = useRef<HTMLDivElement>(null);
    const contentBoxReference = useRef<HTMLDivElement>(null);

    const {
        categories,
        products,
        isSuccess,
        relatedQueries,
    } = useLuigisboxAutocomplete(query, categoryPath, trackerId, hostname);

    const adjustContentBoxHeight = (): void => {
        if (contentBoxReference.current) {
            const contentBox = contentBoxReference.current;
            const rect = contentBox.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const maxHeight = viewportHeight - rect.top - 70;
            contentBox.style.maxHeight = `${maxHeight}px`;
            contentBox.style.overflowY = "auto";
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        onQueryChange(event.target.value);
        adjustContentBoxHeight();
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Escape") {
            onHide();
        }
    };

    const handleClearSearchQuery = (): void => {
        onQueryChange("");
        inputReference.current?.focus();
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        if (query.trim() === "") {
            event.preventDefault();

            return;
        }

        if (onSubmitForm === undefined) {
            return;
        }

        event.preventDefault();
        onSubmitForm(query);
    };

    const handleShowAutocomplete = (): void => {
        const overlay = document.querySelector<HTMLDivElement>("div.menudrop.modal");
        const autocompleteBoxResults = document.querySelector<HTMLDivElement>("div.autocomplete-box");

        if (overlay) {
            overlay.style.opacity = "0.8";
        }

        if (window.innerWidth < 768) {
            autocompleteBoxResults?.classList.add("active");
        }
    };

    const handleHideAutocomplete = (): void => {
        const overlay = document.querySelector<HTMLDivElement>("div.menudrop.modal");
        const autocompleteBoxResults = document.querySelector<HTMLDivElement>("div.autocomplete-box");

        if (overlay) {
            overlay.style.opacity = "1";
        }

        autocompleteBoxResults?.classList.remove("active");
    };

    const showNotFound = isSuccess && categories.length === 0 && products.length === 0;

    useEffect((): void => {
        if (isOpened) {
            document.body.classList.add("menuactive");
            menuOverlayService.showOverlay((): void => onHide());
            inputReference.current?.focus();
        } else {
            document.body.classList.remove("menuactive");
            menuOverlayService.hideOverlay();

            if (eventTimerId.current !== undefined) {
                clearTimeout(eventTimerId.current);
                eventTimerId.current = undefined;
            }
        }
    }, [isOpened, onHide]);

    useEffect((): void => {
        if (eventTimerId.current !== undefined) {
            clearTimeout(eventTimerId.current);
            eventTimerId.current = undefined;
        }

        if (!isOpened || query.trim().length < 3) {
            return;
        }

        eventTimerId.current = setTimeout((): void => {
            luigisboxAnanyticsService.sendAutocompleteEvent(query, categories, products, categoryPath);
        }, 2000);
    }, [query, categories, products, categoryPath, isOpened]);

    useEffect(() => {
        if (isOpened) {
            handleShowAutocomplete();
        } else {
            handleHideAutocomplete();
        }

        const searchInputElement = document.querySelector<HTMLInputElement>("#search-input");
        const searchButtonElement = document.querySelector<HTMLButtonElement>("#search-button");
        const autocompleteBoxElement = document.querySelector<HTMLDivElement>(".autocomplete-box");

        if (isOpened && searchInputElement && searchButtonElement && autocompleteBoxElement) {
            const inputRect = searchInputElement.getBoundingClientRect();
            const buttonRect = searchButtonElement.getBoundingClientRect();

            autocompleteBoxElement.style.position = "fixed";
            autocompleteBoxElement.style.left = `${inputRect.left}px`;
            autocompleteBoxElement.style.right = `${window.innerWidth - buttonRect.right}px`;
            autocompleteBoxElement.style.top = (window.innerWidth < 768) ? "10px" : `${inputRect.top}px`;
            autocompleteBoxElement.style.width = `${buttonRect.right - inputRect.left}px`;

            adjustContentBoxHeight();
        }
    }, [isOpened]);

    useEffect(() => {
        if (isOpened && resultsBoxReference.current) {
            const resultsBox = resultsBoxReference.current;

            const maxHeight = window.innerHeight * 0.5;
            resultsBox.style.maxHeight = `${maxHeight}px`;
            resultsBox.style.overflowY = "auto";
        }
    }, [isOpened, categories, products]);

    const { data: trendingQueries = [] } = useQuery({
        queryKey: ["trendingQueries"],
        queryFn: async () => {
            const response = await fetch(`https://live.luigisbox.com/v2/trending_queries?tracker_id=${trackerId}&hostname=${hostname}`);
            const data = (await response.json()) as { title: string }[];

            return data.map((item) => item.title);
        },
        staleTime: 1000 * 60 * 60,
        enabled: isOpened,
    });

    if (!isOpened) {
        return undefined;
    }

    return <div className="autocomplete-box">
        <form method="get" action="/vyhledavani" onSubmit={handleSearchSubmit}>
            <div className="autocomplete-input-box">
                <div className="autocomplete-input">
                    <input
                        type="text"
                        name="q"
                        value={query}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        placeholder={t("searchPlaceholder")}
                        ref={inputReference}
                        autoComplete="off"
                        className="autocomplete-input-field"
                    />
                    {query !== "" && <button
                        onClick={handleClearSearchQuery}
                        type="reset"
                        aria-label="Smazat text"
                        className="autocomplete-clear-input">
                        <i className="fa-solid fa-times-circle"></i>
                    </button>}
                    <button type="submit" className="autocomplete-btn-search">
                        <span className="visible-xl visible-lg visible-md visible-sm">{t("searchButton")}</span>
                        <span className="visible-xs"><i className="fa-solid fa-search"></i></span>
                    </button>
                </div>
                <button
                    type="button"
                    onClick={onHide}
                    className="autocomplete-close visible-xs visible-sm"><i className="fa-solid fa-times"></i>
                </button>
            </div>
            <div className="autocomplete-results-box" ref={contentBoxReference}>
                {showNotFound && <p className="autocomplete-no-results">{t("nothingFound")}</p>}
                <div className="autocomplete-box-content" ref={resultsBoxReference}>
                    {query === ""
                        ? (
                            <LuigisboxAutocompleteTrendingBlock
                                trendingQueries={trendingQueries}
                                onTermClick={(term: string): void => {
                                    onQueryChange(term);
                                    inputReference.current?.focus();
                                }}
                            />
                        )
                        : (
                            <>
                                <LuigisboxAutocompleteRelatedBlock
                                    relatedQueries={relatedQueries}
                                    onAnchorClick={(url: string): void => handleAnchorClick(url)}
                                />
                                <LuigisboxAutocompleteProductsBlock
                                    products={products}
                                    onAnchorClick={(url: string): void => handleAnchorClick(url)}
                                />
                                <LuigisboxAutocompleteCategoriesBlock
                                    categories={categories}
                                    onAnchorClick={(url: string): void => handleAnchorClick(url)}
                                />
                            </>
                        )}
                </div>
            </div>
            <div className="autocomplete-submit-box">
                {query !== "" && products.length > 0 && (
                    <button type="submit" className="btn btn-success btn-ac-search autocomplete-show-all">
                        <i className="fa-solid fa-search"></i> zobrazit všechny výsledky
                    </button>
                )}
            </div>
        </form>
    </div>;
}
